import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = [
        "select",
        "toggle",
        "tagsContainer",
        "placeholder",
        "tagsMore",
        "summary",
        "count",
        "dropdown",
        "search",
        "searchClear",
        "actionsRow",
        "selectAll",
        "list",
        "option",
        "empty"
    ];

    static values = {
        variant: { type: String, default: "tags" },
        defaultTagColor: { type: String, default: "cyan" },
        searchable: { type: Boolean, default: true },
        actions: { type: Boolean, default: true },
        direction: { type: String, default: "down" },
        height: { type: Number, default: 320 },
        placeholder: { type: String, default: "Selecione..." },
        url: { type: String, default: "" },
        minChars: { type: Number, default: 3 },
        searchParam: { type: String, default: "q" },
        valueKey: { type: String, default: "id" },
        textKey: { type: String, default: "label" }
    };

    connect() {
        this.boundOnClickOutside = this.onClickOutside.bind(this);
        this.boundOnResize = this.onResize.bind(this);

        document.addEventListener("click", this.boundOnClickOutside);
        window.addEventListener("resize", this.boundOnResize);

        if (typeof ResizeObserver !== "undefined" && this.hasTagsContainerTarget) {
            this.resizeObserver = new ResizeObserver(() => {
                this.onResize();
            });
            this.resizeObserver.observe(this.tagsContainerTarget);
        }

        this.searchTimeout = null;
        this.abortController = null;
        this.isLoading = false;

        this.updateUI();
    }

    disconnect() {
        document.removeEventListener("click", this.boundOnClickOutside);
        window.removeEventListener("resize", this.boundOnResize);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.abortController) {
            this.abortController.abort();
        }
    }

    /* ============================================================
       DROPDOWN TOGGLE & OUTSIDE CLICK
       ============================================================ */

    toggle(event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const isOpen = this.dropdownTarget.classList.contains("show");
        if (isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    open() {
        this.element.classList.add("show");
        this.dropdownTarget.classList.add("show");
        this.toggleTarget.setAttribute("aria-expanded", "true");

        if (this.hasSearchTarget) {
            setTimeout(() => {
                this.searchTarget.focus();
            }, 50);
        }
    }

    close() {
        this.element.classList.remove("show");
        this.dropdownTarget.classList.remove("show");
        this.toggleTarget.setAttribute("aria-expanded", "false");
        this.resetSearch(false);
    }

    onClickOutside(event) {
        if (!this.element.contains(event.target)) {
            this.close();
        }
    }

    onResize() {
        if (this.variantValue === "tags") {
            this.fitTags();
        }
    }

    stopPropagation(event) {
        event.stopPropagation();
    }

    /* ============================================================
       SEARCH & FILTER
       ============================================================ */

    onSearchInput(event) {
        const query = event.target.value.trim();

        if (this.hasSearchClearTarget) {
            this.searchClearTarget.style.display = query.length > 0 ? "inline" : "none";
        }

        if (this.urlValue) {
            this.handleRemoteSearch(query);
        } else {
            this.handleLocalFilter(query);
        }
    }

    onSearchKeyDown(event) {
        if (event.key === "Escape") {
            this.close();
        }
    }

    clearSearch(event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        this.resetSearch(true);
    }

    resetSearch(focus = false) {
        if (this.hasSearchTarget) {
            this.searchTarget.value = "";
            if (focus) {
                this.searchTarget.focus();
            }
        }

        if (this.hasSearchClearTarget) {
            this.searchClearTarget.style.display = "none";
        }

        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = null;
        }

        if (this.urlValue) {
            this.handleRemoteSearch("");
        } else {
            this.handleLocalFilter("");
        }
    }

    normalizeString(str) {
        return (str || "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
    }

    handleLocalFilter(query) {
        const q = this.normalizeString(query.trim());
        let visibleCount = 0;

        this.optionTargets.forEach((opt) => {
            const label = this.normalizeString(opt.getAttribute("data-label") || opt.textContent);
            const matches = !q || label.includes(q);

            if (matches) {
                opt.classList.remove("d-none");
                opt.classList.add("d-flex");
                visibleCount++;
            } else {
                opt.classList.add("d-none");
                opt.classList.remove("d-flex");
            }
        });

        if (this.hasEmptyTarget) {
            this.emptyTarget.classList.toggle("d-none", visibleCount > 0);
        }
    }

    handleRemoteSearch(query) {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        if (query.length < this.minCharsValue) {
            return;
        }

        this.searchTimeout = setTimeout(() => {
            this.fetchRemoteOptions(query);
        }, 300);
    }

    async fetchRemoteOptions(query) {
        if (this.abortController) {
            this.abortController.abort();
        }

        this.abortController = new AbortController();
        this.showLoading();

        try {
            const sep = this.urlValue.includes("?") ? "&" : "?";
            const requestUrl = `${this.urlValue}${sep}${encodeURIComponent(this.searchParamValue)}=${encodeURIComponent(query)}`;

            const response = await fetch(requestUrl, {
                signal: this.abortController.signal,
                headers: { "X-Requested-With": "XMLHttpRequest" }
            });

            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`);
            }

            const data = await response.json();
            this.renderRemoteResults(Array.isArray(data) ? data : (data.items || data.results || []));
        } catch (err) {
            if (err.name !== "AbortError") {
                console.error("MultiSelect remote search failed:", err);
            }
        } finally {
            this.hideLoading();
        }
    }

    showLoading() {
        if (this.hasEmptyTarget) {
            this.emptyTarget.classList.add("d-none");
        }
        if (!this.loadingEl) {
            this.loadingEl = document.createElement("li");
            this.loadingEl.className = "d-flex align-items-center justify-content-center py-3 text-muted small";
            this.loadingEl.innerHTML = '<div class="spinner-border spinner-border-sm mr-2" role="status"></div>Carregando...';
        }
        this.listTarget.appendChild(this.loadingEl);
    }

    hideLoading() {
        if (this.loadingEl && this.loadingEl.parentNode) {
            this.loadingEl.remove();
        }
    }

    renderRemoteResults(items) {
        const selectedValues = new Set(this.getSelectedOptions().map((o) => String(o.value)));

        // Remove options não selecionadas antigas
        this.optionTargets.forEach((opt) => {
            const val = opt.getAttribute("data-value");
            if (!selectedValues.has(val)) {
                opt.remove();
            }
        });

        items.forEach((item) => {
            const val = String(item[this.valueKeyValue] !== undefined ? item[this.valueKeyValue] : item.id);
            const label = String(item[this.textKeyValue] !== undefined ? item[this.textKeyValue] : (item.text || item.label || val));

            const alreadyExists = this.optionTargets.some((opt) => opt.getAttribute("data-value") === val);

            if (!alreadyExists) {
                const li = this.createOptionElement(val, label, selectedValues.has(val));
                this.listTarget.appendChild(li);
            }
        });

        if (this.hasEmptyTarget) {
            const currentOptions = this.optionTargets;
            this.emptyTarget.classList.toggle("d-none", currentOptions.length > 0);
            this.listTarget.appendChild(this.emptyTarget);
        }
    }

    createOptionElement(value, label, isSelected = false) {
        const li = document.createElement("li");
        li.className = `dropdown-item d-flex align-items-center py-1 px-2 user-select-none ${isSelected ? "font-weight-bold" : ""}`;
        li.style.cursor = "pointer";
        li.setAttribute("data-value", value);
        li.setAttribute("data-label", label);
        li.setAttribute("data-multi-select-target", "option");
        li.setAttribute("data-action", "click->multi-select#selectOption");
        li.setAttribute("role", "option");
        li.setAttribute("aria-selected", isSelected ? "true" : "false");

        li.innerHTML = `
            <div class="custom-control custom-checkbox w-100" style="pointer-events: none;">
                <input type="checkbox" class="custom-control-input" ${isSelected ? "checked" : ""}>
                <label class="custom-control-label mb-0 d-block"><span class="d-block text-truncate">${label}</span></label>
            </div>
        `;

        return li;
    }

    get selectElement() {
        if (this.hasSelectTarget) {
            return this.selectTarget;
        }
        const prev = this.element.previousElementSibling;
        if (prev && (prev.tagName === "SELECT" || prev.matches('select[data-multi-select-target="select"]'))) {
            return prev;
        }
        return this.element.querySelector("select");
    }

    /* ============================================================
       SELECTION MANAGEMENT
       ============================================================ */

    selectOption(event) {
        const opt = event.currentTarget;
        if (opt.classList.contains("disabled")) {
            return;
        }

        const value = opt.getAttribute("data-value");
        const label = opt.getAttribute("data-label");
        const isSelected = opt.getAttribute("aria-selected") === "true" || opt.querySelector('input[type="checkbox"]')?.checked;

        this.setOptionSelected(value, label, !isSelected);
        this.updateUI();
        this.dispatchChangeEvent();
    }

    setOptionSelected(value, label, selected) {
        // Atualiza select nativo
        const select = this.selectElement;
        if (select) {
            let option = Array.from(select.options).find((o) => o.value === value);

            if (!option && selected) {
                option = new Option(label || value, value, true, true);
                select.add(option);
            }

            if (option) {
                option.selected = selected;
            }
        }

        // Atualiza elemento visual li
        this.optionTargets.forEach((opt) => {
            if (opt.getAttribute("data-value") === value) {
                const checkbox = opt.querySelector('input[type="checkbox"]');
                if (checkbox) {
                    checkbox.checked = selected;
                }
                if (selected) {
                    opt.classList.add("font-weight-bold");
                    opt.setAttribute("aria-selected", "true");
                } else {
                    opt.classList.remove("alert-primary", "font-weight-bold", "fw-semibold", "fw-bold");
                    opt.setAttribute("aria-selected", "false");
                }
            }
        });
    }

    toggleSelectAll(event) {
        if (event) {
            event.preventDefault();
        }

        const visibleOptions = this.optionTargets.filter(
            (opt) => !opt.classList.contains("d-none") && !opt.classList.contains("disabled")
        );

        if (visibleOptions.length === 0) {
            return;
        }

        const allVisibleSelected = visibleOptions.every((opt) => {
            return opt.getAttribute("aria-selected") === "true" || opt.querySelector('input[type="checkbox"]')?.checked;
        });
        const shouldSelectAll = !allVisibleSelected;

        visibleOptions.forEach((opt) => {
            const val = opt.getAttribute("data-value");
            const label = opt.getAttribute("data-label");
            this.setOptionSelected(val, label, shouldSelectAll);
        });

        this.updateUI();
        this.dispatchChangeEvent();
    }

    clearAll(event) {
        if (event) {
            event.preventDefault();
        }

        const select = this.selectElement;
        if (select) {
            Array.from(select.options).forEach((opt) => {
                opt.selected = false;
            });
        }

        this.optionTargets.forEach((opt) => {
            const checkbox = opt.querySelector('input[type="checkbox"]');
            if (checkbox) {
                checkbox.checked = false;
            }
            opt.classList.remove("alert-primary", "font-weight-bold", "fw-semibold", "fw-bold");
            opt.setAttribute("aria-selected", "false");
        });

        this.updateUI();
        this.dispatchChangeEvent();
    }

    removeTag(value) {
        this.setOptionSelected(value, "", false);
        this.updateUI();
        this.dispatchChangeEvent();
    }

    getSelectedOptions() {
        const select = this.selectElement;
        if (select) {
            return Array.from(select.selectedOptions).map((opt) => ({
                value: opt.value,
                label: opt.text || opt.value
            }));
        }

        return this.optionTargets
            .filter((opt) => opt.getAttribute("aria-selected") === "true" || opt.querySelector('input[type="checkbox"]')?.checked)
            .map((opt) => ({
                value: opt.getAttribute("data-value"),
                label: opt.getAttribute("data-label")
            }));
    }

    dispatchChangeEvent() {
        const select = this.selectElement;
        if (select) {
            select.dispatchEvent(new Event("change", { bubbles: true }));
            select.dispatchEvent(new Event("input", { bubbles: true }));
        }
    }

    /* ============================================================
       UI UPDATE & TAG RENDERING
       ============================================================ */

    updateUI() {
        const selected = this.getSelectedOptions();

        this.renderCountBadge(selected.length);
        this.updateSelectAllIndicator(selected.length);
        this.updateValidationState();

        if (this.variantValue === "tags") {
            this.renderTags(selected);
            this.fitTags();
        } else {
            this.renderCount(selected);
        }
    }

    updateValidationState() {
        const select = this.selectElement;
        if (!select) return;

        const isValid = select.checkValidity ? select.checkValidity() : (select.required ? select.selectedOptions.length > 0 : true);

        if (isValid) {
            this.element.classList.remove("is-invalid");
            if (this.hasToggleTarget) {
                this.toggleTarget.classList.remove("is-invalid");
            }
        }
    }

    getBadgeClass() {
        const colorMap = {
            cyan: "badge-info alert-info",
            blue: "badge-primary alert-primary",
            green: "badge-success alert-success",
            yellow: "badge-warning alert-warning",
            red: "badge-danger alert-danger",
            secondary: "badge-secondary alert-secondary",
            dark: "badge-dark",
            light: "badge-light"
        };
        return colorMap[this.defaultTagColorValue] || "badge-info";
    }

    renderTags(selected) {
        if (!this.hasTagsContainerTarget) return;

        // Remove tags existentes
        const existingTags = this.tagsContainerTarget.querySelectorAll(".badge-item-tag");
        existingTags.forEach((t) => t.remove());

        if (selected.length === 0) {
            if (this.hasPlaceholderTarget) {
                this.placeholderTarget.style.display = "inline";
            }
            if (this.hasTagsMoreTarget) {
                this.tagsMoreTarget.classList.add("d-none");
                this.tagsMoreTarget.style.setProperty("display", "none", "important");
            }
            return;
        }

        if (this.hasPlaceholderTarget) {
            this.placeholderTarget.style.display = "none";
        }

        const badgeClass = this.getBadgeClass();

        selected.forEach((item) => {
            const tag = document.createElement("span");
            tag.className = `badge ${badgeClass} multiselect-tag-${this.defaultTagColorValue} badge-item-tag align-items-center py-1 px-2 text-truncate fw-medium`;
            tag.setAttribute("data-value", item.value);
            tag.setAttribute("title", item.label);

            const label = document.createElement("span");
            label.className = "text-truncate";
            label.textContent = item.label;

            const removeBtn = document.createElement("button");
            removeBtn.type = "button";
            removeBtn.className = "close ml-1 p-0 text-reset flex-shrink-0";
            removeBtn.setAttribute("aria-label", `Remover ${item.label}`);
            removeBtn.setAttribute("title", `Remover ${item.label}`);
            removeBtn.innerHTML = '<span aria-hidden="true">&times;</span>';

            removeBtn.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.removeTag(item.value);
            });

            removeBtn.addEventListener("keydown", (e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    this.removeTag(item.value);
                }
            });

            tag.appendChild(label);
            tag.appendChild(removeBtn);

            if (this.hasTagsMoreTarget) {
                this.tagsContainerTarget.insertBefore(tag, this.tagsMoreTarget);
            } else {
                this.tagsContainerTarget.appendChild(tag);
            }
        });
    }

    fitTags() {
        if (this.variantValue !== "tags" || !this.hasTagsContainerTarget) {
            return;
        }

        const tags = Array.from(this.tagsContainerTarget.querySelectorAll(".badge-item-tag"));
        const more = this.hasTagsMoreTarget ? this.tagsMoreTarget : null;

        if (tags.length === 0) {
            if (more) {
                more.classList.add("d-none");
                more.style.setProperty("display", "none", "important");
            }
            return;
        }

        const available = this.tagsContainerTarget.clientWidth;
        if (!available) {
            return;
        }

        const GAP = 4;
        const MIN_TAG_WIDTH = 80;
        const total = tags.length;

        // Resetar tags para estado visível sem restrições
        tags.forEach((tag) => {
            tag.classList.remove("d-none");
            tag.style.removeProperty("display");
            tag.style.maxWidth = "";
            tag.style.flexShrink = "0";
        });

        if (more) {
            more.classList.add("d-none");
            more.style.setProperty("display", "none", "important");
        }

        // Medir larguras naturais exatas desacopladas do container
        const naturalWidths = tags.map((tag) => {
            const prevPos = tag.style.position;
            const prevVis = tag.style.visibility;
            const prevMax = tag.style.maxWidth;

            tag.style.position = "absolute";
            tag.style.visibility = "hidden";
            tag.style.maxWidth = "none";
            tag.style.display = "inline-flex";

            const width = Math.ceil(tag.getBoundingClientRect().width || tag.offsetWidth || 0);

            tag.style.position = prevPos;
            tag.style.visibility = prevVis;
            tag.style.maxWidth = prevMax;

            return width;
        });

        // Medir largura do badge '...'
        let moreWidth = 26;
        if (more) {
            more.classList.remove("d-none");
            more.style.removeProperty("display");
            more.style.display = "inline-flex";
            moreWidth = Math.ceil(more.getBoundingClientRect().width || more.offsetWidth || 26);
            more.classList.add("d-none");
            more.style.setProperty("display", "none", "important");
        }

        // 1. Verificar se TODAS as tags cabem inteiras (Caso 1)
        let totalFullWidth = 0;
        for (let i = 0; i < total; i++) {
            totalFullWidth += (i > 0 ? GAP : 0) + naturalWidths[i];
        }

        if (totalFullWidth <= available) {
            // Caso 1: Todas as opções cabem inteiras
            tags.forEach((tag) => {
                tag.classList.remove("d-none");
                tag.style.display = "inline-flex";
                tag.style.maxWidth = "";
            });
            if (more) {
                more.classList.add("d-none");
                more.style.setProperty("display", "none", "important");
            }
            return;
        }

        // 2. Nem todas cabem. Calcular quantas tags inteiras cabem com '...'
        const availableWithMore = available - moreWidth - GAP;
        let usedWithMore = 0;
        let countFullWithMore = 0;

        for (let i = 0; i < total; i++) {
            const gap = i > 0 ? GAP : 0;
            if (usedWithMore + gap + naturalWidths[i] <= availableWithMore) {
                usedWithMore += gap + naturalWidths[i];
                countFullWithMore = i + 1;
            } else {
                break;
            }
        }

        // Caso 2: Pelo menos 2 tags inteiras cabem com '...' (ex: [opcao1][opcao2][opcao3]...[4])
        if (countFullWithMore >= 2) {
            for (let i = 0; i < countFullWithMore; i++) {
                tags[i].classList.remove("d-none");
                tags[i].style.display = "inline-flex";
                tags[i].style.maxWidth = "";
            }
            for (let i = countFullWithMore; i < total; i++) {
                tags[i].classList.add("d-none");
                tags[i].style.setProperty("display", "none", "important");
            }
            if (more) {
                more.classList.remove("d-none");
                more.style.setProperty("display", "inline-flex", "important");
            }
            return;
        }

        // Se apenas 1 tag cheia coube com '...':
        if (countFullWithMore === 1) {
            const usedFirst = naturalWidths[0];
            const remainingForSecond = available - usedFirst - GAP;

            // Caso 3: A segunda tag pode ser truncada na borda se houver espaço razoável (>= MIN_TAG_WIDTH)
            // ex: [opcao 1 longa][opcao 2...][3]
            if (total > 1 && remainingForSecond >= MIN_TAG_WIDTH) {
                tags[0].classList.remove("d-none");
                tags[0].style.display = "inline-flex";
                tags[0].style.maxWidth = "";

                tags[1].classList.remove("d-none");
                tags[1].style.display = "inline-flex";
                tags[1].style.maxWidth = `${Math.floor(remainingForSecond)}px`;

                for (let i = 2; i < total; i++) {
                    tags[i].classList.add("d-none");
                    tags[i].style.setProperty("display", "none", "important");
                }
                if (more) {
                    more.classList.add("d-none");
                    more.style.setProperty("display", "none", "important");
                }
                return;
            } else {
                // Não cabe segunda tag truncada com segurança: mostra Tag 0 + '...' (Caso 2)
                tags[0].classList.remove("d-none");
                tags[0].style.display = "inline-flex";
                tags[0].style.maxWidth = "";

                for (let i = 1; i < total; i++) {
                    tags[i].classList.add("d-none");
                    tags[i].style.setProperty("display", "none", "important");
                }
                if (more) {
                    more.classList.remove("d-none");
                    more.style.setProperty("display", "inline-flex", "important");
                }
                return;
            }
        }

        // Nem a primeira tag coube inteira com '...':
        if (total > 1 && availableWithMore >= 50) {
            tags[0].style.maxWidth = `${Math.floor(availableWithMore)}px`;
            tags[0].classList.remove("d-none");
            tags[0].style.display = "inline-flex";

            for (let i = 1; i < total; i++) {
                tags[i].classList.add("d-none");
                tags[i].style.setProperty("display", "none", "important");
            }
            if (more) {
                more.classList.remove("d-none");
                more.style.setProperty("display", "inline-flex", "important");
            }
        } else {
            tags[0].style.maxWidth = `${Math.floor(available)}px`;
            tags[0].classList.remove("d-none");
            tags[0].style.display = "inline-flex";

            for (let i = 1; i < total; i++) {
                tags[i].classList.add("d-none");
                tags[i].style.setProperty("display", "none", "important");
            }
            if (more) {
                more.classList.add("d-none");
                more.style.setProperty("display", "none", "important");
            }
        }
    }

    renderCount(selected) {
        const count = selected.length;

        if (this.hasSummaryTarget) {
            if (count === 0) {
                this.summaryTarget.textContent = this.placeholderValue;
                this.summaryTarget.classList.add("text-muted");
            } else if (count === 1) {
                this.summaryTarget.textContent = selected[0].label;
                this.summaryTarget.classList.remove("text-muted");
            } else {
                this.summaryTarget.textContent = `${count} itens selecionados`;
                this.summaryTarget.classList.remove("text-muted");
            }
        }
    }

    renderCountBadge(count) {
        if (this.hasCountTarget) {
            if (count > 0) {
                this.countTarget.textContent = String(count);
                this.countTarget.style.display = "inline-block";
            } else {
                this.countTarget.style.display = "none";
            }
        }
    }

    updateSelectAllIndicator(selectedCount) {
        if (!this.hasSelectAllTarget) return;

        const total = this.optionTargets.filter(
            (opt) => !opt.classList.contains("disabled")
        ).length;

        const checkbox = this.selectAllTarget.querySelector('input[type="checkbox"]');
        if (checkbox) {
            checkbox.checked = selectedCount >= total && total > 0;
            checkbox.indeterminate = selectedCount > 0 && selectedCount < total;
        }
    }
}
